"use client";

import dynamic from "next/dynamic";
import type { ComponentType } from "react";

import type { SkinId } from "../_lib/siteConfig";
import { useSite } from "../_lib/useSite";

/*
 * 화면 레지스트리 — 스킨 × 화면 → 컴포넌트 (`docs/base/11-스킨-체계.md` §5-2).
 *
 * 🔴 **라우트 파일(`app/<경로>/page.tsx`)은 껍데기입니다.** `<Screen name="coupons" />`
 *    한 줄이고, 실제 화면은 여기서 **활성 스킨의 것만** `next/dynamic` 으로 불러옵니다.
 *    그래서 한 스킨을 쓰는 몰에 다른 스킨의 화면 코드가 내려가지 않습니다.
 *    🔑 확인: 브라우저 개발자 도구 네트워크 탭에서 `_skins_default_screens_*` (개발) 또는
 *    그 화면의 청크만 내려오고 `_skins_simple_` 이 없는지 봅니다.
 *
 * 🔴 **`next/dynamic` 은 한 줄씩 적습니다.** `import()` 경로가 문자열 상수여야 번들러가
 *    청크를 나눕니다 — 반복문으로 경로를 만들면 모든 화면이 한 덩어리가 됩니다.
 *
 * 🔑 **없는 화면은 `default` 로 폴백합니다.** 새 스킨이 화면을 하나씩 채워 가는 동안에도
 *    몰은 빈 화면 없이 뜹니다. 서버 렌더(SSR)도 그대로라 첫 HTML 에 화면이 들어 있습니다.
 *
 * 🔑 **스킨을 더하는 법**: `_skins/<이름>/screens/*.tsx` 를 만들고 아래 `SCREENS` 에 그 스킨의
 *    줄을 더합니다. `_lib/siteConfig.ts` 의 `READY_SKINS` 와 `_skins/tokens.css` 에도 한 줄씩.
 */
export type ScreenKey =
  | "home"
  | "products"
  | "product"
  | "checkout"
  | "coupons"
  | "coupon"
  | "orders"
  | "order"
  | "orderCancel"
  | "orderDone"
  | "login"
  | "register"
  | "me"
  | "mePassword"
  | "seller"
  | "sellerFranchises"
  | "sellerFranchiseNew"
  | "sellerFranchiseRate"
  | "sellerMembers"
  | "sellerSettlements";

/*
 * 🔴 **`loading` 옵션을 주지 않습니다 — 일부러입니다** (2026-09-23 실측).
 *    `next/dynamic` 은 `loading` 이 있으면 화면을 `<Suspense fallback={loading}>` 으로 감쌉니다.
 *    그러면 서버 HTML 에서 화면이 숨은 `<div hidden>` 으로 따로 흘러오고, React 19.2 의
 *    경계 공개 지연(throttle) 때문에 **첫 그림에 스피너가 약 0.3초 떴다가** 화면이 나옵니다
 *    (기존 몰에는 없던 번쩍임 · `tests/home-hero.mjs` 가 잡았습니다).
 *    `ssr: true` 에 `loading` 이 없으면 Next 는 Suspense 대신 Fragment 로 감싸고 청크 preload 는
 *    그대로 싣습니다 — 서버 HTML 에 화면이 곧바로 들어가고, 하이드레이션은 청크가 올 때까지
 *    서버 HTML 을 그대로 둡니다. 앱 안 이동(전환)에서는 새 화면 청크가 올 때까지 **앞 화면이
 *    남아 있습니다** — 스피너로 바뀌는 것보다 SPA 에 맞습니다.
 */
const DEFAULT_SCREENS: Record<ScreenKey, ComponentType> = {
  home: dynamic(() => import("./default/screens/Home")),
  products: dynamic(() => import("./default/screens/Products")),
  product: dynamic(() => import("./default/screens/Product")),
  checkout: dynamic(() => import("./default/screens/Checkout")),
  coupons: dynamic(() => import("./default/screens/Coupons")),
  coupon: dynamic(() => import("./default/screens/Coupon")),
  orders: dynamic(() => import("./default/screens/Orders")),
  order: dynamic(() => import("./default/screens/Order")),
  orderCancel: dynamic(() => import("./default/screens/OrderCancel")),
  orderDone: dynamic(() => import("./default/screens/OrderDone")),
  login: dynamic(() => import("./default/screens/Login")),
  register: dynamic(() => import("./default/screens/Register")),
  me: dynamic(() => import("./default/screens/Me")),
  mePassword: dynamic(() => import("./default/screens/MePassword")),
  seller: dynamic(() => import("./default/screens/Seller")),
  sellerFranchises: dynamic(() => import("./default/screens/SellerFranchises")),
  sellerFranchiseNew: dynamic(() => import("./default/screens/SellerFranchiseNew")),
  sellerFranchiseRate: dynamic(() => import("./default/screens/SellerFranchiseRate")),
  sellerMembers: dynamic(() => import("./default/screens/SellerMembers")),
  sellerSettlements: dynamic(() => import("./default/screens/SellerSettlements")),
};

/*
 * `simple` — `docs/design/simple-skin/README.md` (시안 15장 + 부속 화면 5개 · 2026-09-23).
 * 🔑 스무 화면 전부 있어 `default` 로 폴백하는 칸이 없습니다.
 */
const SIMPLE_SCREENS: Record<ScreenKey, ComponentType> = {
  home: dynamic(() => import("./simple/screens/Home")),
  products: dynamic(() => import("./simple/screens/Products")),
  product: dynamic(() => import("./simple/screens/Product")),
  checkout: dynamic(() => import("./simple/screens/Checkout")),
  coupons: dynamic(() => import("./simple/screens/Coupons")),
  coupon: dynamic(() => import("./simple/screens/Coupon")),
  orders: dynamic(() => import("./simple/screens/Orders")),
  order: dynamic(() => import("./simple/screens/Order")),
  orderCancel: dynamic(() => import("./simple/screens/OrderCancel")),
  orderDone: dynamic(() => import("./simple/screens/OrderDone")),
  login: dynamic(() => import("./simple/screens/Login")),
  register: dynamic(() => import("./simple/screens/Register")),
  me: dynamic(() => import("./simple/screens/Me")),
  mePassword: dynamic(() => import("./simple/screens/MePassword")),
  seller: dynamic(() => import("./simple/screens/Seller")),
  sellerFranchises: dynamic(() => import("./simple/screens/SellerFranchises")),
  sellerFranchiseNew: dynamic(() => import("./simple/screens/SellerFranchiseNew")),
  sellerFranchiseRate: dynamic(() => import("./simple/screens/SellerFranchiseRate")),
  sellerMembers: dynamic(() => import("./simple/screens/SellerMembers")),
  sellerSettlements: dynamic(() => import("./simple/screens/SellerSettlements")),
};

/** 🔑 `default` 외의 스킨은 가진 화면만 적습니다(`Partial`). 빈 칸은 `default` 가 채웁니다. */
const SCREENS: { default: Record<ScreenKey, ComponentType> } & Partial<Record<Exclude<SkinId, "default">, Partial<Record<ScreenKey, ComponentType>>>> = {
  default: DEFAULT_SCREENS,
  simple: SIMPLE_SCREENS,
};

export function Screen({ name }: { name: ScreenKey }) {
  const { skin } = useSite();
  const Component = (skin === "default" ? undefined : SCREENS[skin]?.[name]) ?? SCREENS.default[name];
  return <Component />;
}
